import React from 'react'
import {useDispatch} from 'react-redux'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {Text, TextVariant} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {getOrGenerateAnonymousId} from '@vio/core/src/_web/anonymousId'
import {isApple} from '@vio/core/src/_web/utils/userAgent'
import {trackLoginClick} from '@vio/core/src/auth/modules/actions'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {useLogIn} from '@vio/shared/src/authentication/hooks/useLogIn'

import {
  AnimatedAuthIllustration,
  ILLUSTRATION_HEIGHT
} from './AnimatedAuthIllustration'

interface Props {
  isRowLayout?: boolean
}

const BUTTON_MAX_WIDTH = '256px'

const Container = styled.div<{withBorder: boolean}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: ${({withBorder}) =>
    withBorder ? `1px solid ${cssTheme.colors.border.brand.tertiary}` : 'none'};
  border-radius: ${cssTheme.layout.radius.lg};
  background: ${cssTheme.colors.background.brand.tertiary};
  cursor: pointer;
`
const ButtonContainer = styled(Button)<{isRowLayout: boolean}>`
  max-width: ${({isRowLayout}) => (isRowLayout ? BUTTON_MAX_WIDTH : '100%')};
`
/**
 * Apple sign-in is only available on Apple devices,
 * so an Android or Windows visitor must not see it.
 */
const ProviderIcons = () => {
  const isHydrated = useIsHydrated()
  const isAppleDevice = isApple() && isHydrated

  return (
    <Box
      data-id="LoginBannerProviderIcons"
      display="flex"
      alignItems="center"
      gap={cssTheme.layout.spacing.s100}
    >
      {isAppleDevice && <Icon name="Apple" size="sm" />}
      <Icon name="GoogleColor" size="sm" />
      <Icon name="FacebookColor" size="sm" />
    </Box>
  )
}

const ReminderTitle = ({variant}: {variant: TextVariant}) => (
  <Text variant={variant} tag="p">
    <FormattedMessageWrapper
      id="loginBanner.title"
      defaultMessage="Sign in to see the best prices"
    />
  </Text>
)

const MobileLayout = ({
  onClick
}: {
  onClick: (e: React.SyntheticEvent) => void
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    width="100%"
    padding={cssTheme.layout.spacing.s300}
  >
    <Box display="flex" alignItems="center" gap={cssTheme.layout.spacing.s250}>
      <AnimatedAuthIllustration height={ILLUSTRATION_HEIGHT.mobile} />
      <ReminderTitle variant="labelS" />
    </Box>
    <Button variant="secondary" size="md" onClick={onClick}>
      <FormattedMessageWrapper id="signInButton" defaultMessage="Sign in" />
    </Button>
  </Box>
)

const DesktopLayout = ({
  onClick,
  isRowLayout
}: {
  isRowLayout: boolean
  onClick: (e: React.SyntheticEvent) => void
}) => (
  <Box
    display="flex"
    alignItems={isRowLayout ? 'center' : 'flex-start'}
    justifyContent="space-between"
    width="100%"
    gap={cssTheme.layout.spacing.s500}
    padding={cssTheme.layout.spacing.s500}
    flexDirection={isRowLayout ? 'row' : 'column'}
  >
    <Box display="flex" alignItems="center" gap={cssTheme.layout.spacing.s300}>
      <AnimatedAuthIllustration height={ILLUSTRATION_HEIGHT.desktop} />
      <Box display="flex" flexDirection="column">
        <ReminderTitle variant="titleM" />
        <Text variant={isRowLayout ? 'bodyS' : 'labelXS'}>
          <FormattedMessageWrapper
            id="loginBanner.subtitle"
            defaultMessage="It only takes a second to unlock our best deals."
          />
        </Text>
      </Box>
    </Box>
    <ButtonContainer
      variant="tertiary"
      size="lg"
      fullWidth
      onClick={onClick}
      isRowLayout={isRowLayout}
      iconEnd={<ProviderIcons />}
    >
      <Text variant="labelM" tag="span">
        <FormattedMessageWrapper
          id="loginBanner.signInWithProvidersButton"
          defaultMessage="Sign in with"
        />
      </Text>
    </ButtonContainer>
  </Box>
)

export const LoginBanner = ({isRowLayout = false}: Props) => {
  const {isMobile} = useDeviceLayout()
  const logIn = useLogIn()
  const dispatch = useDispatch()

  const handleSignIn = () => {
    dispatch(trackLoginClick(SourceComponentType.RspLoginBanner))

    logIn({
      anonymousId: getOrGenerateAnonymousId(),
      flow: AuthenticationFlows.Login,
      sourceComponent: SourceComponentType.RspLoginBanner,
      onAuthSuccessLandingUrl: window.location.href
    })
  }

  const handleButtonClick = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    handleSignIn()
  }

  return (
    <Container withBorder={!isRowLayout} onClick={handleSignIn}>
      {isMobile ? (
        <MobileLayout onClick={handleButtonClick} />
      ) : (
        <DesktopLayout onClick={handleButtonClick} isRowLayout={isRowLayout} />
      )}
    </Container>
  )
}
